import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Globe, Zap, Plane, Building2, Users, Fuel, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { 
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

const StevenProfile = () => {
  const user = {
    name: "Steven Jast",
    role: "Director, Quebec Economic Diplomacy - Southern U.S.",
    email: "steven.jast@mfa.gov.tr",
    solutionBounties: 6,
    domains: [
      "Economic Diplomacy",
      "International Trade",
      "Clean Energy",
      "Aerospace",
      "Critical Minerals",
      "Strategic Partnerships",
      "Export Development",
      "Market Intelligence",
      "Cross-Border Investment"
    ],
    industries: [
      { name: "Energy", icon: Zap },
      { name: "Aerospace", icon: Plane },
      { name: "Government", icon: Building2 },
      { name: "International Trade", icon: Globe },
      { name: "Mining & Resources", icon: Fuel },
      { name: "Economic Development", icon: Users }
    ],
    confirmedSolutions: 15,
    confirmedKnowledgeBases: 32,
    tokenBalance: 120,
    bio: "Steven Jast serves as Network Ambassador for Quebec's economic diplomacy mission in the Southern United States, leading strategic initiatives to strengthen trade relationships across Texas, Louisiana, Arkansas, and Oklahoma. He specializes in identifying opportunities for Quebec companies in key sectors including clean energy, aerospace, and advanced manufacturing. His work focuses on building bridges between Quebec's innovation ecosystem and the dynamic markets of the Southern U.S., facilitating knowledge transfer and fostering bilateral economic cooperation."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/9ab60236-1dee-4a33-9559-cba52f34b5ca.png"
                    imageAlt="Steven Jast"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/9ab60236-1dee-4a33-9559-cba52f34b5ca.png" 
                        alt="Steven Jast"
                      />
                      <AvatarFallback>SJ</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 hover-scale"
                        >
                          BIO
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[500px] p-4 animate-fade-in">
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold">Steven Jast</h4>
                          <div className="text-sm text-gray-700 space-y-2">
                            <p>Steven Jast was appointed Québec's Delegate in Houston on November 11, 2024. He represents Québec in Texas, Louisiana, Arkansas, and Oklahoma.</p>
                            <p>Mr. Jast has over 20 years of experience in economic development and entrepreneurship.</p>
                            <p>Mr. Jast founded ROI (Research On Investment) in 2003 and Gazelle.ai in 2017.</p>
                            <p>He is a recognized thought leader on the fundamentals and best practices related to investment attraction and business retention and expansion, as well as advanced digital marketing, lead nurturing, and engagement techniques for economic development organizations. He has written several guidebooks and provided training on best practices relating to investment attraction for international governments at all levels.</p>
                            <p>Steven was recognized as a finalist in the international Ernst & Young Entrepreneur of the Year competition in 2008 and in 2019.</p>
                            <p>He is an avid fitness enthusiast and die-hard Montréal Canadiens fan.</p>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 hover-scale"
                        >
                          NW
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-96 p-4 animate-fade-in">
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold">Steven's Knowledge Network</h4>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/eglantine-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/a095a62e-32dd-481e-94c0-dfa151a021f9.png" alt="Eglantine" className="object-cover" />
                                  <AvatarFallback>EP</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Eglantine</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/karen-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/1747ca6f-b961-4ea7-bae4-a5d133e2a21b.png" alt="Karen" />
                                  <AvatarFallback>KA</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Karen</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/pete-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/75937035-16a7-4dc5-88cc-58dd952f6c60.png" alt="Pete" />
                                  <AvatarFallback>PE</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Pete</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/trey-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/34971d27-7908-4c61-ac9e-881493ec551d.png" alt="Trey" />
                                  <AvatarFallback>TR</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Trey</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/ozlem-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/a044a687-d14a-497e-9bb5-eec5d53ab9aa.png" alt="Ozlem" className="object-cover" />
                                  <AvatarFallback>OT</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Ozlem</span>
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ImageHoverPopup 
                      imageSrc="/lovable-uploads/9ab60236-1dee-4a33-9559-cba52f34b5ca.png"
                      imageAlt="Steven Jast"
                    >
                      <h2 className="font-serif text-3xl font-bold text-black cursor-pointer hover:text-primary transition-colors">{user.name}</h2>
                    </ImageHoverPopup>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="flex items-center mb-4">
                    <p className="text-lg font-medium text-black">{user.role}</p>
                    <Link to="/quebec-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d41ea10b-9167-451e-944e-ce5900958bf1.png" 
                        alt="Quebec Economic Development" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link
                          to="/solution-bounties/steven"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={8.9} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StevenProfile;